<template>
  <div class="w-full max-w-3xl" :dir="isRtl === true ? 'rtl' : 'ltr'">
    <div class="rounded-2xl border border-stone-200 bg-white/90 p-5 shadow-sm dark:border-stone-700 dark:bg-stone-900/70">
      <div class="flex flex-wrap items-start justify-between">
        <div>
          <h3 class="font-semibold text-slate-800 dark:text-slate-100">{{ t('agentCreate.integrations.title', 'Connect integrations') }}</h3>
          <p class="mt-1 text-sm text-slate-600 dark:text-slate-400">{{ t('agentCreate.integrations.description', 'Select tools the agent may use. You can skip this and add integrations later.') }}</p>
        </div>
        <button type="button" class="btn btn-sm mt-3 md:mt-0" :disabled="isLoading === true" @click="loadTools">
          <Icon icon="tabler:refresh" width="16" :class="isLoading === true ? 'animate-spin' : ''" />
          {{ t('common.refresh', 'Refresh') }}
        </button>
      </div>

      <div v-if="loadError !== ''" class="alert alert-warning mt-4 text-sm">
        <Icon icon="tabler:alert-triangle" width="18" />
        <span>{{ loadError }}</span>
      </div>

      <div v-else-if="isLoading === true" class="flex justify-center py-10">
        <span class="loading loading-spinner loading-md"></span>
      </div>

      <div v-else-if="availableTools.length === 0" class="py-10 text-center text-sm text-slate-500 dark:text-slate-400">
        {{ t('agentCreate.integrations.empty', 'No integrations are available for this organization.') }}
      </div>

      <div v-else class="mt-5">
        <div v-for="tool in availableTools" :key="tool.id" class="rounded-xl border border-base-300 p-4 mt-3 first:mt-0">
          <label class="flex cursor-pointer items-start">
            <input :checked="isToolSelected(tool.id) === true" type="checkbox" class="checkbox mt-1" @change="toggleTool(tool)" />
            <div class="min-w-0 flex-1 ml-3">
              <div class="font-medium text-slate-800 dark:text-slate-100">{{ tool.name }}</div>
              <p class="mt-1 text-sm text-slate-600 dark:text-slate-400">{{ tool.description }}</p>
            </div>
          </label>
          <div v-if="isToolSelected(tool.id) === true && tool.actions.length > 0" class="mt-4 border-t border-base-300 pt-4">
            <div class="text-sm font-medium">{{ t('agentCreate.integrations.actions', 'Actions to allow') }}</div>
            <label v-for="action in tool.actions" :key="action.id" class="label cursor-pointer justify-start">
              <input :checked="isActionSelected(tool.id, action.id) === true" type="checkbox" class="checkbox" @change="toggleAction(tool.id, action.id)" />
              <span class="label-text ml-2">{{ action.label }}</span>
            </label>
          </div>
          <label v-if="isToolSelected(tool.id) === true" class="label cursor-pointer justify-start mt-3 border-t border-base-300 pt-4">
            <input :checked="waitForToolResponse(tool.id) === true" type="checkbox" class="checkbox" @change="toggleWaitForResponse(tool.id)" />
            <span class="label-text ml-2">{{ t('agentCreate.integrations.waitForResponse', 'Wait for the integration result before replying') }}</span>
          </label>
        </div>
      </div>

      <div v-if="selectedTools.length > 0" class="mt-5 rounded-xl bg-base-200 p-4">
        <div class="font-medium">{{ t('agentCreate.integrations.summary', 'Selected integrations') }}</div>
        <div class="mt-2 flex flex-wrap -m-1">
          <span v-for="tool in selectedTools" :key="tool.id" class="badge badge-neutral m-1">{{ tool.name }}{{ selectedActionLabel(tool) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { Icon } from '@iconify/vue/dist/offline';
import axios from '@/api/axiosInstance';
import { globalState } from '@/globalState';
import { useI18n } from '@/i18n';

const emit = defineEmits(['update:integrations']);
const { t, currentLanguage, availableLanguages } = useI18n();
const availableTools = ref([]);
const selectedTools = ref([]);
const selectedActions = ref({});
const waitForResponse = ref({});
const isLoading = ref(false);
const loadError = ref('');

const isRtl = computed(() => {
  const language = availableLanguages.value.find((item) => item.code === currentLanguage.value);
  if (language === undefined || language === null) {
    return false;
  }
  return language.dir === 'rtl';
});

function getToolId(tool) {
  if (tool !== undefined && tool !== null && tool.toolid !== undefined && tool.toolid !== null) {
    return String(tool.toolid);
  }
  if (tool !== undefined && tool !== null && tool.toolId !== undefined && tool.toolId !== null) {
    return String(tool.toolId);
  }
  if (tool !== undefined && tool !== null && tool.toolGuid !== undefined && tool.toolGuid !== null) {
    return String(tool.toolGuid);
  }
  if (tool !== undefined && tool !== null && tool.id !== undefined && tool.id !== null) {
    return String(tool.id);
  }
  return '';
}

function getToolData(tool) {
  if (tool !== undefined && tool !== null && tool.tooldata !== undefined && tool.tooldata !== null && typeof tool.tooldata === 'object') {
    return tool.tooldata;
  }
  if (tool !== undefined && tool !== null && tool.toolData !== undefined && tool.toolData !== null && typeof tool.toolData === 'object') {
    return tool.toolData;
  }
  return {};
}

function normalizeActions(rawActions) {
  const actions = Array.isArray(rawActions) === true ? rawActions : [];
  const normalized = [];
  let index = 0;
  while (index < actions.length) {
    const action = actions[index];
    if (typeof action === 'string' && action.trim().length > 0) {
      normalized.push({ id: action.trim(), label: action.trim() });
    }
    if (action !== null && typeof action === 'object') {
      const actionId = action.id !== undefined && action.id !== null ? String(action.id) : (action.name !== undefined && action.name !== null ? String(action.name) : '');
      const actionLabel = action.label !== undefined && action.label !== null ? String(action.label) : (action.name !== undefined && action.name !== null ? String(action.name) : actionId);
      if (actionId.length > 0) {
        normalized.push({ id: actionId, label: actionLabel });
      }
    }
    index = index + 1;
  }
  return normalized;
}

function normalizeTool(tool) {
  const id = getToolId(tool);
  const data = getToolData(tool);
  const nameCandidate = tool !== undefined && tool !== null && tool.toolname !== undefined ? tool.toolname : (tool !== undefined && tool !== null && tool.toolName !== undefined ? tool.toolName : (tool !== undefined && tool !== null ? tool.name : ''));
  const descriptionCandidate = data.description !== undefined ? data.description : (tool !== undefined && tool !== null && tool.description !== undefined ? tool.description : '');
  const actionSource = data.actions !== undefined ? data.actions : (data.availableActions !== undefined ? data.availableActions : data.functions);
  return {
    id,
    name: typeof nameCandidate === 'string' && nameCandidate.length > 0 ? nameCandidate : id,
    description: typeof descriptionCandidate === 'string' ? descriptionCandidate : '',
    actions: normalizeActions(actionSource)
  };
}

function emitIntegrations() {
  const selectedToolIds = [];
  const toolSettings = {};
  let index = 0;
  while (index < selectedTools.value.length) {
    const tool = selectedTools.value[index];
    selectedToolIds.push(tool.id);
    const actions = Array.isArray(selectedActions.value[tool.id]) === true ? selectedActions.value[tool.id] : [];
    toolSettings[tool.id] = { actions, waitForResponse: waitForToolResponse(tool.id) === true };
    index = index + 1;
  }
  emit('update:integrations', { selectedToolIds, toolSettings, selectedTools: selectedTools.value.slice() });
}

function isToolSelected(toolId) {
  return selectedTools.value.some((tool) => tool.id === toolId) === true;
}

function isActionSelected(toolId, actionId) {
  const actions = selectedActions.value[toolId];
  return Array.isArray(actions) === true && actions.includes(actionId) === true;
}

function waitForToolResponse(toolId) {
  return waitForResponse.value[toolId] === true;
}

function toggleTool(tool) {
  if (isToolSelected(tool.id) === true) {
    selectedTools.value = selectedTools.value.filter((item) => item.id !== tool.id);
    const nextActions = { ...selectedActions.value };
    delete nextActions[tool.id];
    selectedActions.value = nextActions;
    const nextWaitForResponse = { ...waitForResponse.value };
    delete nextWaitForResponse[tool.id];
    waitForResponse.value = nextWaitForResponse;
  } else {
    selectedTools.value = selectedTools.value.concat([tool]);
  }
  emitIntegrations();
}

function toggleAction(toolId, actionId) {
  const currentActions = Array.isArray(selectedActions.value[toolId]) === true ? selectedActions.value[toolId] : [];
  const nextActions = currentActions.includes(actionId) === true ? currentActions.filter((item) => item !== actionId) : currentActions.concat([actionId]);
  selectedActions.value = { ...selectedActions.value, [toolId]: nextActions };
  emitIntegrations();
}

function toggleWaitForResponse(toolId) {
  waitForResponse.value = { ...waitForResponse.value, [toolId]: waitForToolResponse(toolId) !== true };
  emitIntegrations();
}

function selectedActionLabel(tool) {
  const actions = Array.isArray(selectedActions.value[tool.id]) === true ? selectedActions.value[tool.id] : [];
  if (actions.length === 0) {
    return '';
  }
  return ` (${actions.length})`;
}

async function loadTools() {
  isLoading.value = true;
  loadError.value = '';
  try {
    const response = await axios.get(`${globalState.apiEndpoint}/api/tools/getAllAITools`, { params: { includeSystemTools: true } });
    const tools = Array.isArray(response.data) === true ? response.data : [];
    const normalizedTools = [];
    let index = 0;
    while (index < tools.length) {
      const normalizedTool = normalizeTool(tools[index]);
      if (normalizedTool.id.length > 0) {
        normalizedTools.push(normalizedTool);
      }
      index = index + 1;
    }
    availableTools.value = normalizedTools;
  } catch (error) {
    availableTools.value = [];
    loadError.value = t('agentCreate.integrations.loadFailed', 'Could not load integrations. You can continue and configure them later.');
  } finally {
    isLoading.value = false;
  }
}

onMounted(() => {
  loadTools();
});

defineExpose({
  isReady: () => true
});
</script>
